import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import Lottie from 'lottie-react';
import { PageHeader } from '../../../components/partials';
import Datepicker from '../../../components/widgets/Datepicker';
import css from './CreativeGalleryHeader.css';
import AutoCompleteField from '../../../components/molecules/AutoCompleteField';
import downloadFileAnimation from '../../../resources/animations/download_file.json';
import Tooltip from '../../../components/molecules/Tooltip';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import Icon, { IconSizes, IconTypes } from '../../../components/foundations/Icon';
import BookmarkPopoverContainer from './BookmarkPopoverContainer';
import { useAsync } from '../../../utils/customHooks';
import { Fields } from '../../../services';
import { useUrlParams } from '../../../utils/useUrlParamsHook';
import { AutoCompleteSizes } from '../../../components/molecules/AdvancedAutoComplete/consts';
import { updatedField } from '../../../actions/fields';
import { setActionType, updateBookmarkData } from '../../../actions/reports';
import { reportsWithoutConfig } from '../../../utils/reports';
import { initialStateBookmark } from '../../../reducers/reports';
import { Button, Tabs } from '../../../components/widgets';
import { CreativeFilters, CreativeTabs, TRACK_EVENT_PREFIX } from '../../consts';
import { INSIGHTS_KEY_PREFIX, INSIGHTS_TAB, MIN_DATE_RANGE_DAYS } from '../creativeInsights/consts';
import { trackMixpanelEvent } from '../../../utils/general';

const SUPPORT_LINK =
    'https://support.singular.net/hc/en-us/articles/43756881255451-Creative-Gallery-FAQ';
export const APPS_LABEL = 'STATIC.PLACEHOLDERS.SELECT_APP';
export const SOURCES_LABEL = 'STATIC.PLACEHOLDERS.SELECT_SOURCE';
export const EXPORT_CSV_RUN_QUERY_MESSAGE = 'STATIC.PAGES.CREATIVE_GALLERY.EXPORT_CSV_TOOLTIP.RUN_QUERY_MESSAGE';
export const EXPORT_CSV_NO_DATA_MESSAGE = 'STATIC.PAGES.CREATIVE_GALLERY.EXPORT_CSV_TOOLTIP.NO_DATA_MESSAGE';
export const SAVE_CREATIVES_RAW_DATA = 'STATIC.PAGES.CREATIVE_GALLERY.EXPORT_CSV_TOOLTIP.SAVE_CREATIVES_RAW_DATA';
export const SAVE_INSIGHTS_RAW_DATA = 'STATIC.PAGES.CREATIVE_GALLERY.EXPORT_CSV_TOOLTIP.SAVE_INSIGHTS_RAW_DATA';
export const METRICS_LABEL = 'STATIC.METRICS';
export const EVENTS_AND_PERIODS_LABEL = 'STATIC.PLACEHOLDERS.COHORTED_METRICS_AND_EVENTS';

const HeaderAutoComplete = memo(({ onChange, value, size, isMulti = false, ...props }) => (
    <AutoCompleteField
        searchable
        clearable
        onChange={onChange}
        advanced
        size={size}
        value={isMulti ? value : value ? [value] : []}
        isMulti={isMulti}
        {...props}
    />
));

HeaderAutoComplete.propTypes = {
    onChange: PropTypes.func.isRequired,
    isMulti: PropTypes.bool,
    value: PropTypes.oneOfType([AutoCompleteOptionType, PropTypes.arrayOf(AutoCompleteOptionType)]),
    size: PropTypes.oneOf(Object.values(AutoCompleteSizes)),
};

const LOTTIE_OPTIONS = {
    animationData: downloadFileAnimation,
    rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
    },
    style: { height: '40px' },
};

const fieldsAPI = new Fields();

const CreativeGalleryHeader = ({
    apps = [],
    sources = [],
    metrics,
    startDate = '',
    endDate = '',
    app,
    source,
    isLoadingApps,
    isLoadingSources,
    onFilterChange,
    selectedMetrics,
    hasQueryBeenDone = false,
    creativesAmount = 0,
    onCsvDownloadClicked,
    translate,
    isLoadingCsvExport = false,
    isLoadingMetrics,
    cohortFieldsOptions = [],
    selectedCohorts,
    isLoadingCohorts = false,
    cohortsGroupBy,
    selectedTab,
    onTabChange,
    onExtendDateRange,
}) => {
    const [{ data: bookmarksResponse }, fetchBookmarks] = useAsync();
    const { getParamsObject } = useUrlParams();
    const dispatch = useDispatch();

    const bookmarks = useSelector(state => state.fields.bookmarks || []);
    const activeBookmark = useSelector(state => state.creativeGallery.bookmark);
    const isSidenavSlim = useSelector(state => state.settings.isSidenavSlim);

    const isExportDisabled = !hasQueryBeenDone || !creativesAmount;

    const csvExportTitle = useMemo(() => {
        if (!hasQueryBeenDone) {
            return translate(EXPORT_CSV_RUN_QUERY_MESSAGE);
        }

        if (!creativesAmount) {
            return translate(EXPORT_CSV_NO_DATA_MESSAGE);
        }

        return translate(selectedTab === INSIGHTS_TAB ? SAVE_INSIGHTS_RAW_DATA : SAVE_CREATIVES_RAW_DATA);
    }, [hasQueryBeenDone, creativesAmount, selectedTab, translate]);

    const selectedAppOption = apps?.find(option => option.value === app);
    const selectedSourceOption = sources?.find(option => option.value === source);

    const { bookmark_id: bookmarkId } = getParamsObject();
    const autoCompleteSize = !isSidenavSlim ? AutoCompleteSizes.Small : AutoCompleteSizes.Medium;

    const isDateRangeTooSmall = useMemo(() => {
        if (!startDate || !endDate) return false;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const timeDiff = end.getTime() - start.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
        return daysDiff < MIN_DATE_RANGE_DAYS - 1;
    }, [startDate, endDate]);

    useEffect(() => {
        !bookmarks.length && fetchBookmarks(fieldsAPI.updateBookmarks());
    }, [fetchBookmarks]);

    useEffect(() => {
        if (!bookmarksResponse) return;

        dispatch(updatedField('bookmarks', bookmarksResponse.bookmarks || []));
    }, [bookmarksResponse, dispatch]);

    useEffect(() => {
        if (!bookmarks?.length || bookmarkId === activeBookmark.uuid) return;

        const bookmark = (bookmarkId && bookmarks.find(({ uuid }) => bookmarkId === uuid)) || initialStateBookmark;
        dispatch(setActionType(updateBookmarkData(bookmark), reportsWithoutConfig.creativeGallery));
    }, [bookmarks, bookmarkId, dispatch]);

    const handleTabChange = useCallback(
        tabID => {
            trackMixpanelEvent(TRACK_EVENT_PREFIX, 'Change Tab', {
                selectedTab: tabID,
                app,
                source,
                startDate,
                endDate,
            });
            onTabChange(tabID);
        },
        [app, source, startDate, endDate]
    );

    return (
        <PageHeader
            text="STATIC.PAGES.CREATIVE_GALLERY.PAGE_NAME"
            sticky
            stickyShadow
            shouldAddPadding
            subText={{
                faqLink: SUPPORT_LINK,
            }}
            additionalText={activeBookmark.name}
        >
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Tabs
                    tabs={CreativeTabs}
                    selected={selectedTab}
                    onClick={tabID => handleTabChange(tabID)}
                    containerClass={css.tabsContainer}
                />
                <div className={css.content}>
                    <div className={css.filters}>
                        <Datepicker
                            buttonContainerClass={css.input}
                            startDate={startDate}
                            endDate={endDate}
                            onApply={data =>
                                onFilterChange(CreativeFilters.DATE_RANGE, {
                                    startDate: data.start_date,
                                    endDate: data.end_date,
                                })
                            }
                        />
                        <HeaderAutoComplete
                            options={apps}
                            value={selectedAppOption}
                            label={APPS_LABEL}
                            loading={isLoadingApps}
                            disabled={!!isLoadingApps}
                            onChange={selected => onFilterChange(CreativeFilters.APP, selected?.value)}
                            clearable={apps.length > 1}
                            size={autoCompleteSize}
                        />
                        <HeaderAutoComplete
                            options={sources}
                            value={selectedSourceOption}
                            label={SOURCES_LABEL}
                            loading={isLoadingSources}
                            disabled={!!isLoadingSources}
                            onChange={selected => onFilterChange(CreativeFilters.SOURCE, selected?.value)}
                            clearable
                            size={autoCompleteSize}
                        />
                        {selectedTab !== INSIGHTS_TAB && (
                            <>
                                <HeaderAutoComplete
                                    options={metrics}
                                    value={selectedMetrics}
                                    label={METRICS_LABEL}
                                    loading={isLoadingMetrics}
                                    onChange={selected =>
                                        onFilterChange(
                                            CreativeFilters.METRICS,
                                            selected.map(metric => metric.value)
                                        )
                                    }
                                    isMulti
                                    size={autoCompleteSize}
                                />
                                <HeaderAutoComplete
                                    options={cohortFieldsOptions}
                                    value={selectedCohorts}
                                    loading={isLoadingCohorts}
                                    label={EVENTS_AND_PERIODS_LABEL}
                                    onChange={selected => onFilterChange(CreativeFilters.COHORTS, selected)}
                                    isMulti
                                    lazy
                                    groupBy={cohortsGroupBy}
                                    size={autoCompleteSize}
                                />
                            </>
                        )}
                    </div>
                    <div className={css.actionButtons}>
                        <BookmarkPopoverContainer
                            hasQueryBeenDone={hasQueryBeenDone}
                            activeBookmark={activeBookmark}
                            startDate={startDate}
                            endDate={endDate}
                            disabled={selectedTab === INSIGHTS_TAB}
                        />
                        <Tooltip contentClass={css.tooltip} title={csvExportTitle} position="bottom">
                            <div className={css.downloadCsvContainer}>
                                {isLoadingCsvExport ? (
                                    <Lottie {...LOTTIE_OPTIONS} />
                                ) : (
                                    <Icon
                                        name="file-download"
                                        type={IconTypes.TABLER}
                                        disabled={isExportDisabled}
                                        onClick={onCsvDownloadClicked}
                                    />
                                )}
                            </div>
                        </Tooltip>
                    </div>
                </div>
                {selectedTab === INSIGHTS_TAB && (
                    <div className={css.dateWarningContainer}>
                        {isDateRangeTooSmall && (
                            <div className={css.dateRangeWarning}>
                                <Icon
                                    type={IconTypes.MINGCUTE}
                                    name="alert-line"
                                    size={IconSizes.SMALL}
                                    className={css.warningIcon}
                                />
                                <div className={css.warning}>
                                    {translate(`${INSIGHTS_KEY_PREFIX}.SMALL_DATE_RANGE_MESSAGE`, {
                                        min_date_range_days: MIN_DATE_RANGE_DAYS,
                                    })}
                                </div>
                                {onExtendDateRange && (
                                    <Button
                                        onClick={onExtendDateRange}
                                        className={css.extendButton}
                                        type="link"
                                        text="Extend now"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </PageHeader>
    );
};

CreativeGalleryHeader.propTypes = {
    apps: PropTypes.arrayOf(AutoCompleteOptionType),
    sources: PropTypes.arrayOf(AutoCompleteOptionType),
    metrics: PropTypes.arrayOf(AutoCompleteOptionType),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    app: PropTypes.string,
    source: PropTypes.string,
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    isLoadingApps: PropTypes.bool,
    isLoadingSources: PropTypes.bool,
    isLoadingMetrics: PropTypes.bool,
    onFilterChange: PropTypes.func,
    onCsvDownloadClicked: PropTypes.func,
    creativesAmount: PropTypes.number,
    hasQueryBeenDone: PropTypes.bool,
    translate: PropTypes.func,
    isLoadingCsvExport: PropTypes.bool,
    cohortFieldsOptions: PropTypes.arrayOf(AutoCompleteOptionType),
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
    cohortsGroupBy: PropTypes.func,
    isLoadingCohorts: PropTypes.bool,
    selectedTab: PropTypes.string,
    onTabChange: PropTypes.func,
    onExtendDateRange: PropTypes.func,
};

export default withLocalize(CreativeGalleryHeader);
